<template>
  <div class="op-wrap">
    <div class="op-header">
      <div>
        <h1 class="op-title">Мої замовлення</h1>
        <p class="op-sub">Історія всіх ваших ремонтів</p>
      </div>
      <router-link to="/orders/create" class="op-btn-new">+ Нове замовлення</router-link>
    </div>

    <!-- Загрузка -->
    <div v-if="loading" class="op-loading">
      <div class="op-spinner"></div>
      Завантажуємо ваші замовлення...
    </div>

    <!-- Нет заказов -->
    <div v-else-if="orders.length === 0" class="op-empty">
      <div class="op-empty-icon">📋</div>
      <h2>Замовлень поки немає</h2>
      <p>Оформіть перше замовлення — і ми швидко полагодимо ваш телефон</p>
      <router-link to="/orders/create" class="op-btn-new">Оформити замовлення</router-link>
    </div>

    <!-- Список заказов -->
    <div v-else class="op-list">
      <div v-for="order in orders" :key="order._id" class="op-card">
        <!-- Шапка карточки -->
        <div class="op-card-header">
          <div class="op-card-num">{{ order.orderNumber }}</div>
          <div class="op-card-date">{{ formatDate(order.createdAt) }}</div>
          <div class="op-status-badge" :class="getStatusClass(order.status)">{{ getStatusLabel(order.status) }}</div>
        </div>

        <!-- Устройство -->
        <div class="op-card-device">
          <div class="op-device-icon">📱</div>
          <div>
            <div class="op-device-name">{{ order.deviceInfo?.brand }} {{ order.deviceInfo?.model }}</div>
            <div class="op-device-issue">{{ order.deviceInfo?.issue }}</div>
          </div>
        </div>

        <!-- Услуги -->
        <div class="op-card-services">
          <div v-for="(item, idx) in order.services" :key="idx" class="op-service-row">
            <span>{{ item.service?.name || item.serviceName || 'Послуга' }}</span>
            <span class="op-service-price">₴{{ item.price }}</span>
          </div>
        </div>

        <!-- Прогресс ремонта -->
        <div class="op-progress-wrap">
          <div class="op-progress-steps">
            <div v-for="(step, i) in statusSteps" :key="i" class="op-ps" :class="getStepClass(order.status, i)">
              <div class="op-ps-dot"></div>
              <div class="op-ps-label">{{ step }}</div>
            </div>
          </div>
          <div class="op-progress-bar">
            <div class="op-progress-fill" :style="{ width: getProgressWidth(order.status) }"></div>
          </div>
        </div>

        <!-- Футер карточки -->
        <div class="op-card-footer">
          <div class="op-payment-status" :class="getPaymentClass(order.paymentStatus)">
            {{ getPaymentLabel(order.paymentStatus) }}
          </div>
          <div class="op-total">₴{{ order.totalPrice }}</div>
          <div class="op-card-actions">
            <router-link :to="{ name: 'OrderDetail', params: { id: order._id } }" class="op-btn-detail">Детальніше</router-link>
            <button v-if="!['completed', 'cancelled'].includes(order.status)" @click="cancelOrder(order._id)" class="op-btn-cancel">Скасувати</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useAuthStore } from '../stores/auth';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

export default {
  name: 'OrdersPage',
  setup() {
    const authStore = useAuthStore();
    const orders = ref([]);
    const loading = ref(false);

    const statusLabels = { pending: 'Очікування', accepted: 'Прийнято', 'in-progress': 'В роботі', completed: 'Завершено', cancelled: 'Скасовано' };
    const paymentLabels = { unpaid: 'Не оплачено', paid: 'Оплачено', partial: 'Частково оплачено' };
    const statusSteps = ['Прийнято', 'Діагностика', 'Ремонт', 'Перевірка', 'Готово'];

    const statusOrder = { pending: 0, accepted: 1, 'in-progress': 2, completed: 4, cancelled: -1 };

    function getStatusLabel(status) { return statusLabels[status] || status; }
    function getStatusClass(status) {
      return { pending: 'status-yellow', accepted: 'status-blue', 'in-progress': 'status-purple', completed: 'status-green', cancelled: 'status-red' }[status] || '';
    }
    function getPaymentLabel(status) { return paymentLabels[status] || status; }
    function getPaymentClass(status) {
      return { unpaid: 'pay-red', paid: 'pay-green', partial: 'pay-yellow' }[status] || '';
    }
    function getStepClass(orderStatus, stepIndex) {
      const cur = statusOrder[orderStatus] ?? -1;
      if (cur < 0) return '';
      if (stepIndex < cur) return 'ps-done';
      if (stepIndex === cur) return 'ps-active';
      return '';
    }
    function getProgressWidth(status) {
      const map = { pending: '5%', accepted: '25%', 'in-progress': '55%', completed: '100%', cancelled: '0%' };
      return map[status] || '0%';
    }
    function formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('ru-UA', { day: 'numeric', month: 'short', year: 'numeric' });
    }

    async function loadOrders() {
      loading.value = true;
      try {
        const res = await axios.get(`${API_URL}/orders`, { headers: { Authorization: `Bearer ${authStore.token}` } });
        orders.value = res.data;
      } catch { orders.value = []; }
      finally { loading.value = false; }
    }

    async function cancelOrder(id) {
      if (!confirm('Вы уверены, что хотите отменить заказ?')) return;
      try {
        await axios.patch(`${API_URL}/orders/${id}/cancel`, {}, { headers: { Authorization: `Bearer ${authStore.token}` } });
        await loadOrders();
      } catch (err) { alert(err.response?.data?.error || 'Ошибка при отмене'); }
    }

    onMounted(loadOrders);
    return { orders, loading, statusSteps, getStatusLabel, getStatusClass, getPaymentLabel, getPaymentClass, getStepClass, getProgressWidth, formatDate, cancelOrder };
  }
};
</script>

<style scoped>
* { box-sizing: border-box; }
.op-wrap { max-width: 900px; margin: 0 auto; }
.op-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 2rem; gap: 1rem; }
.op-title { font-size: 26px; font-weight: 800; color: #26215c; }
.op-sub { font-size: 14px; color: #666; margin-top: 4px; }
.op-btn-new { background: #534ab7; color: #fff; padding: 11px 22px; border-radius: 10px; font-size: 14px; font-weight: 700; text-decoration: none; transition: background 0.15s; white-space: nowrap; display: inline-block; }
.op-btn-new:hover { background: #7f77dd; }

.op-loading { display: flex; align-items: center; gap: 12px; color: #666; padding: 3rem; justify-content: center; font-size: 15px; }
.op-spinner { width: 24px; height: 24px; border: 2px solid #e0dff8; border-top-color: #534ab7; border-radius: 50%; animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

.op-empty { text-align: center; background: #fff; border: 1px solid #e0dff8; border-radius: 20px; padding: 4rem 2rem; }
.op-empty-icon { font-size: 56px; margin-bottom: 1rem; }
.op-empty h2 { font-size: 22px; font-weight: 800; color: #26215c; margin-bottom: 0.5rem; }
.op-empty p { font-size: 14px; color: #666; margin-bottom: 1.5rem; }

.op-list { display: flex; flex-direction: column; gap: 1.25rem; }
.op-card { background: #fff; border: 1px solid #e0dff8; border-radius: 18px; padding: 1.5rem; transition: box-shadow 0.2s; }
.op-card:hover { box-shadow: 0 6px 24px rgba(83,74,183,0.1); }

.op-card-header { display: flex; align-items: center; gap: 12px; margin-bottom: 1rem; padding-bottom: 1rem; border-bottom: 1px solid #f0effc; }
.op-card-num { font-size: 16px; font-weight: 800; color: #26215c; flex: 1; }
.op-card-date { font-size: 13px; color: #aaa; }
.op-status-badge { font-size: 12px; font-weight: 700; padding: 5px 12px; border-radius: 20px; white-space: nowrap; }
.status-yellow { background: #fef9e7; color: #e59f0a; }
.status-blue { background: #eef3ff; color: #3b6ef7; }
.status-purple { background: #eeedfe; color: #534ab7; }
.status-green { background: #e1f5ee; color: #1d9e75; }
.status-red { background: #ffeaea; color: #e05252; }

.op-card-device { display: flex; align-items: center; gap: 12px; margin-bottom: 1rem; }
.op-device-icon { font-size: 24px; width: 44px; height: 44px; background: #f4f3ff; border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.op-device-name { font-size: 15px; font-weight: 700; color: #26215c; }
.op-device-issue { font-size: 13px; color: #666; margin-top: 2px; }

.op-card-services { margin-bottom: 1rem; padding-bottom: 1rem; border-bottom: 1px solid #f0effc; }
.op-service-row { display: flex; justify-content: space-between; font-size: 13px; color: #666; padding: 4px 0; }
.op-service-price { font-weight: 700; color: #1d9e75; }

/* Progress */
.op-progress-wrap { margin-bottom: 1rem; }
.op-progress-steps { display: flex; justify-content: space-between; margin-bottom: 6px; }
.op-ps { display: flex; flex-direction: column; align-items: center; gap: 4px; flex: 1; }
.op-ps-dot { width: 10px; height: 10px; border-radius: 50%; background: #e0dff8; transition: background 0.3s; }
.op-ps.ps-done .op-ps-dot { background: #534ab7; }
.op-ps.ps-active .op-ps-dot { background: #1d9e75; box-shadow: 0 0 0 3px rgba(29,158,117,0.2); }
.op-ps-label { font-size: 10px; color: #ccc; text-align: center; }
.op-ps.ps-done .op-ps-label { color: #534ab7; font-weight: 600; }
.op-ps.ps-active .op-ps-label { color: #1d9e75; font-weight: 700; }
.op-progress-bar { height: 4px; background: #e0dff8; border-radius: 4px; overflow: hidden; }
.op-progress-fill { height: 100%; background: linear-gradient(90deg, #534ab7, #1d9e75); border-radius: 4px; transition: width 0.5s ease; }

/* Footer */
.op-card-footer { display: flex; align-items: center; gap: 10px; padding-top: 1rem; border-top: 1px solid #f0effc; flex-wrap: wrap; }
.op-payment-status { font-size: 12px; font-weight: 700; padding: 4px 10px; border-radius: 20px; }
.pay-red { background: #ffeaea; color: #e05252; }
.pay-green { background: #e1f5ee; color: #1d9e75; }
.pay-yellow { background: #fef9e7; color: #e59f0a; }
.op-total { font-size: 20px; font-weight: 800; color: #26215c; margin-left: auto; }
.op-card-actions { display: flex; gap: 8px; }
.op-btn-detail { background: #eeedfe; color: #534ab7; padding: 8px 16px; border-radius: 8px; font-size: 13px; font-weight: 700; text-decoration: none; transition: background 0.15s; }
.op-btn-detail:hover { background: #e0dff8; }
.op-btn-cancel { background: #ffeaea; color: #e05252; border: none; padding: 8px 14px; border-radius: 8px; font-size: 13px; font-weight: 700; cursor: pointer; transition: background 0.15s; }
.op-btn-cancel:hover { background: #f5c6c6; }

@media (max-width: 600px) {
  .op-header { flex-direction: column; }
  .op-card-footer { flex-direction: column; align-items: flex-start; }
  .op-total { margin-left: 0; }
}
</style>
